/**
 * Topbar Component
 * Phase 2: Product Shell & Navigation
 * 
 * Compact topbar with context controls, theme switcher, and health status.
 * Positioned above the main content area.
 */

import { Icon } from "./Icon";
import { useTheme } from "../contexts/ThemeContext";
import { useHealthCheck } from "../hooks/useHealthCheck";
import { ReviewerThemeBase, ReviewerThemeAccent, REVIEWER_THEME_ACCENT_LABELS } from "../lib/reviewerTheme";
import { useLocale } from "../contexts/LocaleContext";

interface TopbarProps {
  /** Current project context (if any) */
  projectName?: string;
  /** Current audit context (if any) */
  auditName?: string;
  /** Callback to show context selector */
  onSelectContext?: () => void;
  /** Cluster of controls for the HMVM header (no full-width bar). */
  embedded?: boolean;
}

export function Topbar({ projectName, auditName, onSelectContext, embedded }: TopbarProps) {
  const { themeBase, themeAccent, setThemeBase, setThemeAccent } = useTheme();
  const { health, isLoading } = useHealthCheck();
  const { t } = useLocale();
  
  // Resolve theme base (system → dark/light based on OS)
  const resolvedThemeBase = themeBase === "system" 
    ? (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : themeBase;

  return (
    <div
      className="topbar"
      style={{
        height: embedded ? "auto" : "56px",
        borderBottom: embedded ? "none" : "1px solid var(--reviewer-border)",
        backgroundColor: embedded ? "transparent" : "var(--reviewer-bg-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: embedded ? "flex-end" : "space-between",
        padding: embedded ? "0" : "0 20px",
        gap: "16px",
      }}
    >
      {/* Left: Context Display */}
      <div
        className="topbar-context"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: "0 0 auto",
        }}
      >
        {(projectName || auditName) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "13px",
              fontFamily: "var(--reviewer-font-mono)",
              color: "var(--reviewer-text-secondary)",
            }}
          >
            {projectName && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Icon name="folder" size={16} />
                <span>{projectName}</span>
              </div>
            )}
            {projectName && auditName && (
              <span style={{ color: "var(--reviewer-text-tertiary)" }}>/</span>
            )}
            {auditName && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Icon name="play" size={16} />
                <span>{auditName}</span>
              </div>
            )}
          </div>
        )}
        {onSelectContext && !projectName && !auditName && (
          <button
            onClick={onSelectContext}
            className="topbar-context-btn"
            style={{
              background: "none",
              border: "1px solid var(--reviewer-border)",
              padding: "6px 12px",
              cursor: "pointer",
              color: "var(--reviewer-text-secondary)",
              fontSize: "13px",
              fontFamily: "var(--reviewer-font-mono)",
              borderRadius: "0",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Icon name="folder" size={14} />
            <span>{t("common.selectProject")}</span>
          </button>
        )}
      </div>

      {/* Right: Controls */}
      <div
        className="topbar-controls"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: "0 0 auto",
        }}
      >
        {/* Theme Controls */}
        <div
          className="topbar-theme-controls"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingRight: "12px",
            borderRight: "1px solid var(--reviewer-border)",
          }}
        >
          {/* Base Theme Toggle */}
          <button
            onClick={() => {
              const nextBase: ReviewerThemeBase =
                themeBase === "dark" ? "light" : themeBase === "light" ? "system" : "dark";
              setThemeBase(nextBase);
            }}
            className="topbar-theme-btn"
            title={t("common.theme")}
            aria-label={t("common.theme")}
            style={{
              background: "none",
              border: "none",
              padding: "8px",
              cursor: "pointer",
              color: "var(--reviewer-text-secondary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "0",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--reviewer-text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--reviewer-text-secondary)";
            }}
          >
            {themeBase === "system" ? (
              <Icon name="desktop" size={18} />
            ) : resolvedThemeBase === "dark" ? (
              <Icon name="moon" size={18} />
            ) : (
              <Icon name="sun" size={18} />
            )}
          </button>

          {/* Accent Theme Select */}
          <select
            value={themeAccent}
            onChange={(e) => setThemeAccent(e.target.value as ReviewerThemeAccent)}
            className="topbar-accent-select"
            title={t("common.accent")}
            style={{
              background: "var(--reviewer-bg-secondary)",
              border: "1px solid var(--reviewer-border)",
              padding: "6px 8px",
              cursor: "pointer",
              color: "var(--reviewer-text-primary)",
              fontSize: "12px",
              fontFamily: "var(--reviewer-font-mono)",
              borderRadius: "0",
            }}
          >
            {Object.entries(REVIEWER_THEME_ACCENT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Health Status */}
        <div
          className="topbar-health"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "12px",
            fontFamily: "var(--reviewer-font-mono)",
          }}
        >
          {isLoading ? (
            <span
              className="badge badge-info"
              style={{
                padding: "4px 8px",
                fontSize: "11px",
                borderRadius: "0",
                border: "1px solid var(--reviewer-info)",
                backgroundColor: "transparent",
                color: "var(--reviewer-info)",
              }}
            >
              {t("common.loading")}
            </span>
          ) : health ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                className="badge badge-success"
                style={{
                  padding: "4px 8px",
                  fontSize: "11px",
                  borderRadius: "0",
                  border: "1px solid var(--reviewer-success)",
                  backgroundColor: "transparent",
                  color: "var(--reviewer-success)",
                }}
              >
                v{health.version}
              </span>
              <span style={{ color: "var(--reviewer-text-tertiary)", fontSize: "11px" }}>
                {health.environment}
              </span>
            </div>
          ) : (
            <span
              className="badge badge-error"
              style={{
                padding: "4px 8px",
                fontSize: "11px",
                borderRadius: "0",
                border: "1px solid var(--reviewer-danger)",
                backgroundColor: "transparent",
                color: "var(--reviewer-danger)",
              }}
            >
              {t("common.offline")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
